// The home hero's first image, remembered for the next visit: the inline
// script in public/index.html reads this key on "/" and preloads the image
// (same srcset + sizes as the <img>, so the browser reuses the download)
// before the app has even booted. Only https URLs are written or read.

export const HERO_HINT_KEY = 'anum_hero_hint';

const isHttps = (value) => typeof value === 'string' && /^https:\/\/[^\s"'<>]+$/.test(value);

/** srcset candidates are all https ("url 480w, url 720w"). */
const httpsSrcset = (srcset) => typeof srcset === 'string'
  && srcset.split(',').every((part) => isHttps(part.trim().split(/\s+/)[0]));

/** {src, srcset, sizes} worth storing, or null (not https, or no image). */
export const heroHintFor = ({ src, srcset, sizes } = {}) => {
  if (!isHttps(src)) return null;
  const hint = { src };
  if (srcset && httpsSrcset(srcset) && typeof sizes === 'string' && sizes.length < 300) {
    hint.srcset = srcset;
    hint.sizes = sizes;
  }
  return hint;
};

// The first hero photo has arrived (or failed): the home page's other photos
// (feed, blocks further down) wait for it so they never share the line with
// the largest paint. Once per page load; later visits to "/" see it done.
let heroPhotoDone = false;
const heroPhotoListeners = new Set();

export const markHeroPhotoDone = () => {
  if (heroPhotoDone) return;
  heroPhotoDone = true;
  heroPhotoListeners.forEach((listener) => listener());
  heroPhotoListeners.clear();
};

/** Calls `listener` once the hero photo is in (at once if it already is). */
export const onHeroPhotoDone = (listener) => {
  if (heroPhotoDone) {
    listener();
    return () => {};
  }
  heroPhotoListeners.add(listener);
  return () => heroPhotoListeners.delete(listener);
};

/** Remember (or forget) the first hero image. Storage errors are ignored. */
export const writeHeroHint = (attrs) => {
  try {
    const hint = heroHintFor(attrs || {});
    if (hint) window.localStorage.setItem(HERO_HINT_KEY, JSON.stringify(hint));
    else window.localStorage.removeItem(HERO_HINT_KEY);
  } catch { /* storage blocked: the hint is only a speed-up */ }
};
